// Other
import { DateTime } from 'luxon'

import {
  ActivityPage,
  TimelineActivity,
  parseActivity,
} from '../activity/models/activity'
import { Area, AreaID } from '../areas/models/area'
import { Building, BuildingID } from '../buildings/models/building'
import {
  Pagination,
  PaginationDto,
  paginationFromDto,
} from '../common/models/pagination'
import { ShiftOfferStatus } from '../employee_shift/models/employeeShiftOffer'
import { Employee, EmployeeID } from '../employees/models/employee'
import { JobRole, JobRoleID } from '../job_roles/models/jobRole'
import { ShiftReasonID } from '../shift_reasons/models/shiftReason'
// Models
import { SiteID } from '../sites/models/site'
import { Skill, SkillID, SkillJson, skillFromDto } from '../skills/models/skill'
import { Shift, ShiftDto, ShiftID, shiftFromDto } from './models/shift'

import axiosInstance from '../axiosInstance'
import { formatForServer } from '../common/utils/formatDate'
import { IanaZone, IsoDate, LocalTime } from '../shared/dates/types'
import {
  fromDateTime,
  wallTimeToInstant,
} from '../shared/dates/utils/parseDates'
import { ShiftFilter } from './types/types'

/** Start and end of a shift typed as a date and two wall times. An end at or before the start is read as the next day, as in estimateShift. */
function wallTimeRange(
  date: IsoDate,
  startTime: LocalTime,
  endTime: LocalTime,
  zone: IanaZone,
): { start: DateTime; end: DateTime } {
  const start = wallTimeToInstant(date, startTime, zone)
  let end = wallTimeToInstant(date, endTime, zone)
  if (end <= start) {
    end = end.plus({ days: 1 })
  }
  return { start, end }
}

export interface ShiftSearchForm {
  dateFrom?: DateTime
  dateTo?: DateTime
  byCreatedDate?: boolean

  status?: 'active' | 'cancelled' | 'closed'
  fillStatus?: 'all' | 'filled' | 'unfilled' | 'awaiting_approval'

  visibility?: 'all' | 'open' | 'targeted'

  inProgress?: boolean
  inProgressBuffer?: number

  hasResponses?: boolean

  building?: Building | null
  area?: Area | null
  skill?: Skill | null
  jobRole?: JobRole | null
  creator?: Employee | null
  employee?: Employee | null
  employeeId?: EmployeeID | null
  employeeOfferStatus?: ShiftOfferStatus | null

  rateMin?: number
  rateMax?: number

  perPage: number
  page: number

  filter?: ShiftFilter
}

interface ShiftSearchParams {
  shift_date_after?: string
  shift_date_before?: string

  created_from?: string
  created_to?: string

  status?: 'active' | 'cancelled' | 'closed'
  fill_status?: 'all' | 'filled' | 'unfilled' | 'awaiting_approval'

  visibility?: 'open' | 'targeted'

  in_progress?: 0 | 1
  in_progress_buffer?: number

  has_responses?: 0 | 1

  building_id?: string
  area_id?: string
  skill_id?: string
  job_role_id?: string
  creator_id?: string
  employee_id?: string
  employee_offer_status?: string[]

  rate_min?: number
  rate_max?: number

  queue?: 0 | 1

  per_page: number
  page: number
  includes?: string[]

  sort_column?: 'id' | 'start_date' | 'created_at'
  sort_direction?: 'asc' | 'desc'

  filter?: ShiftFilter
}

export interface ShiftUpdateForm {
  shiftId: ShiftID
  areaId?: AreaID
  /** The role an employee needs for the shift; null removes the restriction */
  jobRoleId?: JobRoleID | null
  startDate?: DateTime
  endDate?: DateTime
  note?: string
  assigneeId?: EmployeeID
  /** Why the shift exists; null removes it */
  reasonId?: ShiftReasonID | null
  //
  rateAmount?: number
  open?: boolean
  approvalRequired?: boolean

  timeZoneId?: IanaZone
  date?: IsoDate
  startTime?: LocalTime
  endTime?: LocalTime
}

interface ShiftUpdateData {
  area_id?: AreaID
  job_role_id?: JobRoleID | null
  start_date?: string
  end_date?: string
  note?: string
  assignee_id?: EmployeeID
  reason_id?: ShiftReasonID | null
  //
  rate_amount?: number
  open?: boolean
  approval_required?: boolean
}

function searchFormToParams(form: ShiftSearchForm): ShiftSearchParams {
  const params: ShiftSearchParams = {
    per_page: form.perPage,
    page: form.page,
    // The job role is the card's tag on the schedule
    includes: ['area', 'tags', 'job_role'],
  }

  if (form.inProgress) {
    params.in_progress = 1
    params.in_progress_buffer = form.inProgressBuffer ?? 0
  }

  if (form.hasResponses) {
    params.has_responses = 1
  }

  if (form.building) {
    params.building_id = form.building.id
  }

  if (form.area) {
    params.area_id = form.area.id
  }

  if (form.skill) {
    params.skill_id = form.skill.id
  }

  if (form.jobRole) {
    params.job_role_id = form.jobRole.id
  }

  if (form.creator) {
    params.creator_id = form.creator.id
  }

  if (form.employee) {
    params.employee_id = form.employee.id
  }

  if (form.employeeId) {
    params.employee_id = form.employeeId
  }

  if (form.employeeOfferStatus) {
    params.employee_offer_status = [form.employeeOfferStatus]
  }

  if (form.rateMin) {
    params.rate_min = form.rateMin
  }

  if (form.rateMax) {
    params.rate_max = form.rateMax
  }

  if (form.status) {
    params.status = form.status
  }

  if (form.fillStatus && form.fillStatus !== 'all') {
    params.fill_status = form.fillStatus
  }

  if (form.visibility && form.visibility !== 'all') {
    params.visibility = form.visibility
  }

  if (form.filter) {
    params.filter = form.filter
  }

  const dateFrom = form.dateFrom
  const dateTo = form.dateTo
  if (form.byCreatedDate) {
    if (dateFrom) {
      params.created_from = formatForServer(dateFrom)
    }
    if (dateTo) {
      params.created_to = formatForServer(dateTo)
    }

    params.sort_column = 'created_at'
    params.sort_direction = 'desc'
  } else {
    if (dateFrom) {
      params.shift_date_after = formatForServer(dateFrom)
    }
    if (dateTo) {
      params.shift_date_before = formatForServer(dateTo)
    }
  }

  return params
}

export const fetch = async (
  siteId: SiteID,
  form: ShiftSearchForm,
): Promise<{ shifts: Shift[]; pagination: Pagination }> => {
  const params = searchFormToParams(form)

  const response = await axiosInstance.get(`api/orgs/${siteId}/shifts`, {
    params,
  })

  const data = response.data.data as ShiftDto[]
  const meta = response.data.meta as { pagination: PaginationDto }

  return {
    shifts: data.map((shift) => {
      return shiftFromDto(shift)
    }),
    pagination: paginationFromDto(meta.pagination),
  }
}

export const generateExport = async (
  siteId: SiteID,
  form: ShiftSearchForm,
): Promise<void> => {
  const params = searchFormToParams(form)

  await axiosInstance.post(`api/orgs/${siteId}/shifts/export`, params)
}

export const show = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<{ shift: Shift }> => {
  const params = {
    // The job role is the drawer's title, and the reason is shown under it
    includes: ['area', 'creator', 'job_role', 'reason'],
  }

  const response = await axiosInstance.get(
    `api/orgs/${siteId}/shifts/${shiftId}`,
    { params },
  )

  const data = response.data.data as ShiftDto

  return {
    shift: shiftFromDto(data),
  }
}

function updateFormToData(form: ShiftUpdateForm): ShiftUpdateData {
  const data: ShiftUpdateData = {}

  if (form.areaId) {
    data.area_id = form.areaId
  }

  if (form.jobRoleId !== undefined) {
    data.job_role_id = form.jobRoleId
  }

  if (form.date && form.startTime && form.endTime && form.timeZoneId) {
    const { start, end } = wallTimeRange(
      form.date,
      form.startTime,
      form.endTime,
      form.timeZoneId,
    )

    data.start_date = fromDateTime(start)
    data.end_date = fromDateTime(end)
  } else {
    if (form.startDate) {
      data.start_date = formatForServer(form.startDate)
    }

    if (form.endDate) {
      data.end_date = formatForServer(form.endDate)
    }
  }

  if (form.note) {
    data.note = form.note
  }

  if (form.rateAmount) {
    data.rate_amount = form.rateAmount
  }

  if (form.assigneeId) {
    data.assignee_id = form.assigneeId
  }

  if (form.open !== undefined) {
    data.open = form.open
  }

  if (form.approvalRequired !== undefined) {
    data.approval_required = form.approvalRequired
  }

  if (form.reasonId !== undefined) {
    data.reason_id = form.reasonId
  }

  return data
}

export const update = async (
  siteId: SiteID,
  shiftId: ShiftID,
  form: ShiftUpdateForm,
): Promise<{ shift: Shift }> => {
  const data = updateFormToData(form)

  const response = await axiosInstance.patch(
    `api/orgs/${siteId}/shifts/${shiftId}`,
    data,
  )

  const shiftJson = response.data.data as ShiftDto

  return {
    shift: shiftFromDto(shiftJson),
  }
}

export const publish = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<void> => {
  await axiosInstance.put(`api/orgs/${siteId}/shifts/${shiftId}/publish`)
}

export const unpublish = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<void> => {
  await axiosInstance.delete(`api/orgs/${siteId}/shifts/${shiftId}/publish`)
}

export interface ShiftPublishBulkForm {
  shiftIds: ShiftID[]
}

interface ShiftPublishBulkData {
  shift_ids: ShiftID[]
}

/** ShiftPublishBulkRequest accepts at most this many shift ids per request */
export const PUBLISH_BULK_MAX = 100

export const publishBulk = async (
  siteId: SiteID,
  form: ShiftPublishBulkForm,
): Promise<void> => {
  // Sent in batches, one after another; a failed batch stops the rest and the earlier ones stay published
  for (let start = 0; start < form.shiftIds.length; start += PUBLISH_BULK_MAX) {
    const data: ShiftPublishBulkData = {
      shift_ids: form.shiftIds.slice(start, start + PUBLISH_BULK_MAX),
    }

    await axiosInstance.post(`api/orgs/${siteId}/shifts/publish/bulk`, data)
  }
}

export const cancel = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<void> => {
  await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/cancel`)
}

export const destroy = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<void> => {
  await axiosInstance.delete(`api/orgs/${siteId}/shifts/${shiftId}`)
}

export const skillIndex = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<{ skills: Skill[]; pagination: Pagination }> => {
  const response = await axiosInstance.get(
    `api/orgs/${siteId}/shifts/${shiftId}/skills`,
  )

  const data = response.data.data as SkillJson[]
  const meta = response.data.meta as { pagination: PaginationDto }

  return {
    skills: data.map((skill) => {
      return skillFromDto(skill)
    }),
    pagination: paginationFromDto(meta.pagination),
  }
}

export const skillSync = async (
  siteId: SiteID,
  shiftId: ShiftID,
  form: { skillIds: SkillID[] },
): Promise<void> => {
  await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/skills`, {
    skill_ids: form.skillIds,
  })
}

export interface ShiftCreateForm {
  timeZoneId?: IanaZone

  date?: IsoDate
  startTime?: LocalTime
  endTime?: LocalTime

  startDate?: DateTime
  endDate?: DateTime
  shiftDates?: { startDate: DateTime; endDate: DateTime }[]

  published?: boolean

  // Related
  assigneeId?: EmployeeID
  areaId: AreaID
  reasonId?: ShiftReasonID
  jobRoleId?: JobRoleID
  // Rate
  rateAmount?: number
  rateCurrency?: string
  rateType?: string
  // Flags
  approvalRequired?: boolean
  // Employees
  open?: boolean
  employeeIds?: EmployeeID[]
  // Other
  note?: string
  skillIds?: SkillID[]

  queue?: boolean
  queueConfig?: {
    intervalMinutes: number
  }
  queueGroups?: { priority: number; employeeIds: EmployeeID[] }[]
  queueAlt?: unknown
}

interface ShiftCreateData {
  time_zone_id?: string

  start_date?: string
  end_date?: string
  shift_dates?: { start_date: string; end_date: string }[]

  published?: boolean

  // Related
  assignee_id?: string
  area_id: string
  reason_id?: string
  job_role_id?: string
  // Rate
  rate_amount?: number
  rate_currency?: string
  rate_type?: string
  // Flags
  open?: boolean
  approval_required?: boolean
  queue?: boolean
  // Other
  note?: string
  skill_ids?: SkillID[]
  employee_ids?: EmployeeID[]
  // Not implemented
  on_call?: boolean
  urgent?: boolean

  queue_config?: {
    interval_minutes: number
  }
  queue_groups?: { priority: number; employee_ids: EmployeeID[] }[]

  queue_alt?: unknown
}

function createFormToData(form: ShiftCreateForm): ShiftCreateData {
  const data: ShiftCreateData = {
    area_id: form.areaId,
  }

  if (form.shiftDates) {
    data.shift_dates = form.shiftDates.map((shiftDate) => {
      return {
        start_date: formatForServer(shiftDate.startDate, form.timeZoneId),
        end_date: formatForServer(shiftDate.endDate, form.timeZoneId),
      }
    })
  } else if (form.startDate && form.endDate) {
    data.start_date = formatForServer(form.startDate, form.timeZoneId)
    data.end_date = formatForServer(form.endDate, form.timeZoneId)
  } else if (form.date && form.startTime && form.endTime && form.timeZoneId) {
    const { start, end } = wallTimeRange(
      form.date,
      form.startTime,
      form.endTime,
      form.timeZoneId,
    )

    data.start_date = fromDateTime(start)
    data.end_date = fromDateTime(end)
  }

  if (form.published) {
    data.published = true
  }

  if (form.assigneeId) {
    data.assignee_id = form.assigneeId
  }

  if (form.open) {
    data.open = true
  }

  // Sent as false too: left out, the API falls back to the org's default (STG-WEB-18)
  if (form.approvalRequired !== undefined) {
    data.approval_required = form.approvalRequired
  }

  if (form.rateAmount) {
    data.rate_amount = form.rateAmount
  }

  if (form.note) {
    data.note = form.note
  }

  // The backend rejects an empty list (`skill_ids` is sometimes|min:1), so leave it out instead
  if (form.skillIds && form.skillIds.length > 0) {
    data.skill_ids = form.skillIds
  }

  if (form.jobRoleId) {
    data.job_role_id = form.jobRoleId
  }

  if (form.reasonId) {
    data.reason_id = form.reasonId
  }

  if (!form.open && !form.queue && form.employeeIds !== undefined) {
    data.employee_ids = form.employeeIds
  }

  // An open shift has no offer queue: queue without queue_config is a 422, and with it the API would
  // start a queue on an open shift (W6)
  if (form.queue && !form.open) {
    data.queue = true
  }

  if (!form.open && form.queue && form.queueConfig) {
    data.queue_config = {
      interval_minutes: form.queueConfig.intervalMinutes,
    }
  }

  if (!form.open && form.queue && form.queueGroups && !form.queueAlt) {
    data.queue_groups = form.queueGroups.map((group) => {
      return {
        priority: group.priority,
        employee_ids: group.employeeIds,
      }
    })
  }

  if (!form.open && form.queue && form.queueAlt) {
    data.queue_alt = form.queueAlt
  }

  return data
}

export const create = async (
  siteId: SiteID,
  form: ShiftCreateForm,
): Promise<{ shift: Shift }> => {
  const data = createFormToData(form)

  const response = await axiosInstance.post(`api/orgs/${siteId}/shifts`, data)

  const result = response.data.data as ShiftDto

  return {
    shift: shiftFromDto(result),
  }
}

export const createBulk = async (
  siteId: SiteID,
  form: ShiftCreateForm,
): Promise<{ shifts: Shift[] }> => {
  const data = createFormToData(form)

  const response = await axiosInstance.post(
    `api/orgs/${siteId}/shifts/bulk`,
    data,
  )

  const shiftJson = response.data.data as ShiftDto[]

  return {
    shifts: shiftJson.map((shift) => {
      return shiftFromDto(shift)
    }),
  }
}

export const fetchActivity = async (
  siteId: SiteID,
  shiftId: ShiftID,
): Promise<{ activities: TimelineActivity[] }> => {
  // { activities: ShiftActivity[]; pagination: Pagination }
  const params = {
    includes: ['causer'],
  }

  const response = await axiosInstance.get(
    `api/orgs/${siteId}/shifts/${shiftId}/activity`,
    { params },
  )

  // const dataNew = response.data.data
  // console.log('datanew', dataNew)
  // const parsedAll = dataNew.map((data) => {
  //   const parsed = Activity.safeParse(data)
  //   console.log('success', parsed.success)
  //   console.log('parsed', parsed.data)
  //   console.log('error', parsed.error)
  // })

  // const parsed = Activity.safeParse(dataNew[0])
  // console.log('success', parsed.success)
  // console.log('parsed', parsed.data)
  // console.log('error', parsed.error)

  const page = ActivityPage.parse(response.data)
  const activities = page.data.map(parseActivity).filter((e) => e !== null)

  return {
    activities,
  }
  // const data = response.data.data as ShiftActivitySchema[]
  // const meta = response.data.meta as { pagination: PaginationDto }

  // return {
  //   activities: data.map((activity) => {
  //     return ShiftActivity.parse(activity)
  //   }),
  //   pagination: paginationFromDto(meta.pagination),
  // }
}

export const remind = async (
  siteId: SiteID,
  shiftId: ShiftID,
  form: { method: 'push' | 'email' | 'sms' },
): Promise<void> => {
  await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/remind`, {
    method: form.method,
  })
}

export const downloadActivityExport = async (siteId: SiteID): Promise<void> => {
  const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/export`, {
    responseType: 'blob',
  })

  const blobUrl = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = blobUrl
  link.setAttribute('download', 'shifts.xlsx')
  document.body.appendChild(link)
  link.click()

  // Clean up
  window.URL.revokeObjectURL(blobUrl)
  document.body.removeChild(link)
}

export type ShiftCopyForm = {
  sourceDateFrom: DateTime
  sourceDateTo: DateTime
  targetDate: DateTime
  buildingId?: BuildingID | null
}

type ShiftCopyData = {
  source_date_from: string
  source_date_to: string
  target_date: string
  building_id?: string
}

function copyFormToData(form: ShiftCopyForm): ShiftCopyData {
  const data: ShiftCopyData = {
    source_date_from: formatForServer(form.sourceDateFrom),
    source_date_to: formatForServer(form.sourceDateTo),
    target_date: formatForServer(form.targetDate),
    building_id: form.buildingId ?? undefined,
  }

  return data
}

export const copy = async (
  siteId: SiteID,
  form: ShiftCopyForm,
): Promise<void> => {
  const data = copyFormToData(form)

  await axiosInstance.post(`api/orgs/${siteId}/shifts/copy`, data)
}
